import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../components/dashboard-layout";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import {
  ArrowLeft,
  Download,
  FileSpreadsheet,
  FileText,
  User,
  Calendar,
  Hash,
  Building,
  Building2, // Added for Bank
  Globe, // Added for Country
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../../../contexts/auth-context";
import { useTheme } from "../../../contexts/theme-provider";
import {
  handleGetReportById,
  downloadOutputFile,
  downloadInputFile,
} from "../../../services/reports";
import type { Report } from "../../../services/reports";

export default function ReportDetailsPage() {
  // Extending Report type locally to ensure bank/country are recognized if service type is outdated
  const [report, setReport] = useState<
    (Report & { bank_name?: string; country?: string }) | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [inputDownloadUrl, setInputDownloadUrl] = useState<string | null>(null);
  const [inputFileName, setInputFileName] = useState<string | null>(null);
  const [outputDownloadUrl, setOutputDownloadUrl] = useState<string | null>(null);
  const [outputFileName, setOutputFileName] = useState<string | null>(null);

  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isCompleted = report?.status === "completed";
  // const [hasDownloaded, setHasDownloaded] = useState(false);

  useEffect(() => {
    setMounted(true);
    fetchReportDetails();
  }, [params.id]);


  // useEffect(() => {
  //   if (
  //     isCompleted &&
  //     !hasDownloaded &&
  //     report?.id &&
  //     user?.id &&
  //     user?.access_token
  //   ) {
  //     downloadOutputFile(
  //       report.id,
  //       user.id,
  //       report.report_name,
  //       user.access_token,
  //     );

  //     setHasDownloaded(true);
  //   }
  // }, [isCompleted, hasDownloaded, report, user]);

  const fetchReportDetails = async () => {
    try {
      setIsLoading(true);
      if (params.id && user?.access_token) {
        const reportDetails = await handleGetReportById(
          params.id,
          user.access_token,
        );
        setReport(reportDetails);

        // Fetch input file download URL
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/api/v1/reports/${params.id}/download-input/`,
            {
              method: "GET",
              headers: { Authorization: `Bearer ${user.access_token}` },
            }
          );
          if (response.ok) {
            const json = await response.json();
            const downloadUrl = json.data?.download_url || json.download_url;
            const fileName = json.data?.file_name || json.file_name;
            if (downloadUrl) {
              setInputDownloadUrl(downloadUrl);
              if (fileName) {
                setInputFileName(fileName);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching input download URL:", error);
        }

        // Fetch output file download URL
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/api/v1/reports/${params.id}/download/`,
            {
              method: "GET",
              headers: { Authorization: `Bearer ${user.access_token}` },
            }
          );
          if (response.ok) {
            const json = await response.json();
            const downloadUrl = json.data?.download_url || json.download_url;
            if (downloadUrl) {
              setOutputDownloadUrl(downloadUrl);
              setOutputFileName(reportDetails?.report_name || "output.docx");
            }
          }
        } catch (error) {
          console.error("Error fetching output download URL:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching report details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!report?.id || !user?.id || !user?.access_token) return;
    await downloadOutputFile(
      report.id,
      user.id,
      report.report_name,
      user.access_token,
    );
  };

  const handleInputDownload = async () => {
    if (inputDownloadUrl) {
      window.open(inputDownloadUrl, '_blank');
    } else if (!report?.id || !user?.access_token) {
      return;
    } else {
      await downloadInputFile(
        report.id,
        report.input_file_name || "input_file",
        user.access_token,
      );
    }
  };

  // Helper for key-value pairs
  const InfoItem = ({ icon: Icon, label, value, className = "" }: any) => (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span
        className={`text-xs flex items-center gap-1 ${theme === "dark" ? "text-slate-400" : "text-slate-600 dark:text-slate-400"}`}
      >
        {Icon && <Icon className="h-3 w-3" />} {label}
      </span>
      <span
        className={`text-sm font-medium truncate ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}
        title={value}
      >
        {value || "N/A"}
      </span>
    </div>
  );

  if (!mounted) return null;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout>
        <div className="p-4 text-center">
          <p className="mb-2 text-slate-500 dark:text-slate-400">Report not found</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/user/reports")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div
        className={`p-6 min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-slate-50"}`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto space-y-5"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 rounded-full"
                onClick={() => navigate("/user/reports")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1
                className={`text-2xl font-bold ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}
              >
                Report Details
              </h1>
              <Badge
                className={
                  isCompleted
                    ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                    : "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
                }
              >
                {report.status}
              </Badge>
            </div>
          </div>

          {/* Details Card */}
          <Card
            className={`shadow-md ${theme === "dark" ? "bg-slate-800 border-slate-700" : "bg-white"}`}
          >
            <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                Report Metadata
              </CardTitle>
            </CardHeader>

            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4">
                {/* Standard Info */}
                <InfoItem
                  icon={Hash}
                  label="Report Number"
                  value={report.report_no}
                />
                <InfoItem
                  icon={FileText}
                  label="Report Name"
                  value={report.report_name}
                />

                {/* The Requested Fields: Bank & Country */}
                <InfoItem
                  icon={Building2}
                  label="Bank Name"
                  value={report.organization_name}
                  className="bg-blue-50 dark:bg-blue-900/10 p-2 -m-2 rounded-md"
                />
                <InfoItem
                  icon={Globe}
                  label="Country"
                  value={report.country}
                  className="bg-blue-50 dark:bg-blue-900/10 p-2 -m-2 rounded-md"
                />

                <InfoItem
                  icon={Calendar}
                  label="Date Generated"
                  value={report.timestamp ? new Date(report.timestamp).toLocaleString() : new Date().toLocaleString()}
                />
                <InfoItem
                  icon={Building}
                  label="clients"
                  value={report.client_name}
                />
                <InfoItem
                  icon={User}
                  label="Generated By"
                  value={report.username}
                />
                <InfoItem icon={User} label="Email" value={report.email} />
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* File Download Links */}
                <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50 dark:bg-slate-900/50">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <FileSpreadsheet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                        Input File
                      </p>
                      <p className="text-sm font-medium truncate max-w-[200px]">
                        {report.input_file_name}
                      </p>
                    </div>
                  </div>
                  {inputDownloadUrl ? (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a href={inputDownloadUrl} download={inputFileName || undefined}>
                        <Download className="h-3 w-3 mr-2" /> Download
                      </a>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleInputDownload}
                    >
                      <Download className="h-3 w-3 mr-2" /> Download
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50 dark:bg-slate-900/50">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <FileSpreadsheet className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                        Output Report
                      </p>
                      <p className="text-sm font-medium truncate max-w-[200px]">
                        {report.report_name}
                      </p>
                    </div>
                  </div>
                  {outputDownloadUrl ? (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a href={outputDownloadUrl} download={outputFileName || undefined}>
                        <Download className="h-3 w-3 mr-2" /> Download
                      </a>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownload}
                      disabled={!isCompleted}
                    >
                      <Download className="h-3 w-3 mr-2" /> Download
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
